/**
 * FamilyFeed.js — குடும்ப சமூக வலைதளம்
 * C:\Projects\PingMyFamily\web\src\components\FamilyFeed.js
 *
 * Social feed for verified family members.
 * Features: Create post (image/video/text), Like, Comment, Delete own post/comment
 */

import { useState, useEffect, useRef } from 'react';
import {
  Box, VStack, HStack, Text, Textarea, Button,
  Avatar, Spinner, Input, Divider, IconButton,
} from '@chakra-ui/react';
import api from '../services/api';

// ─────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────
function timeAgo(dateStr) {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins  = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days  = Math.floor(diff / 86400000);
  if (mins  < 1)  return 'இப்போது';
  if (mins  < 60) return `${mins} நிமிடம் முன்பு`;
  if (hours < 24) return `${hours} மணி முன்பு`;
  return `${days} நாள் முன்பு`;
}

// ─────────────────────────────────────────
// CreatePost — top of feed
// ─────────────────────────────────────────
function CreatePost({ currentUser, onPostCreated }) {
  const [caption,   setCaption]   = useState('');
  const [mediaList, setMediaList] = useState([]); // [{ media_url, media_type, file }]
  const [uploading, setUploading] = useState(false);
  const [posting,   setPosting]   = useState(false);
  const [error,     setError]     = useState('');
  const fileRef = useRef();

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files);
    setError('');

    for (const file of files) {
      const isVideo = file.type.startsWith('video/');
      const isImage = file.type.startsWith('image/');

      if (!isVideo && !isImage) {
        setError('Only images and videos are allowed');
        continue;
      }

      if (isVideo) {
        // Check duration client-side
        const url = URL.createObjectURL(file);
        const video = document.createElement('video');
        video.src = url;
        await new Promise(resolve => { video.onloadedmetadata = resolve; });
        if (video.duration > 30) {
          setError('Video must be 30 seconds or less');
          URL.revokeObjectURL(url);
          continue;
        }
        URL.revokeObjectURL(url);
      }

      setMediaList(prev => [...prev, {
        file,
        preview: URL.createObjectURL(file),
        media_type: isVideo ? 'video' : 'image',
        uploading: false,
        media_url: null,
      }]);
    }

    // Reset file input
    e.target.value = '';
  };

  const removeMedia = (index) => {
    setMediaList(prev => {
      const updated = [...prev];
      if (updated[index].preview) URL.revokeObjectURL(updated[index].preview);
      updated.splice(index, 1);
      return updated;
    });
  };

  const uploadToR2 = async (file) => {
    // Use /api/photos/upload-media — dedicated feed media endpoint
    // Returns { media_url, media_type } — does NOT touch user profile
    const formData = new FormData();
    formData.append('media', file);
    const res = await api.post('/api/photos/upload-media', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return res.data.media_url;
  };

  const handlePost = async () => {
    if (!caption.trim() && mediaList.length === 0) {
      setError('Caption அல்லது media தேவை / Please add a caption or media');
      return;
    }

    setPosting(true);
    setError('');

    try {
      // Upload all media files to R2
      const uploadedMedia = [];
      for (const m of mediaList) {
        setUploading(true);
        const url = await uploadToR2(m.file);
        uploadedMedia.push({
          media_url:  url,
          media_type: m.media_type,
        });
      }
      setUploading(false);

      // Create post
      await api.post('/api/posts', {
        caption:    caption.trim(),
        visibility: 'family',
        media:      uploadedMedia,
      });

      // Reset form
      setCaption('');
      setMediaList([]);
      onPostCreated();

    } catch (err) {
      setError(err.response?.data?.error || 'பதிவிட முடியவில்லை / Failed to post');
    } finally {
      setPosting(false);
      setUploading(false);
    }
  };

  return (
    <Box
      bg="white" border="1.5px solid" borderColor="purple.200"
      borderRadius="2xl" px={5} py={4}
      boxShadow="0 2px 12px rgba(124,58,237,0.08)"
    >
      {/* Section label */}
      <Text fontSize="13px" fontWeight="700" color="purple.600" mb={3}>
        ✍️ குடும்பத்துடன் பகிரவும்
      </Text>

      <HStack spacing={3} align="flex-start">
        <Avatar size="md" name={currentUser?.name} src={currentUser?.profile_photo}
          border="2px solid" borderColor="purple.300" flexShrink={0} />

        <VStack flex={1} spacing={3} align="stretch">
          <Textarea
            placeholder="என்ன நடக்கிறது? / What's on your mind?"
            value={caption}
            onChange={e => setCaption(e.target.value)}
            bg="purple.50"
            color="purple.900"
            border="1.5px solid"
            borderColor="purple.200"
            borderRadius="xl"
            resize="none"
            rows={3}
            fontSize="14px"
            _placeholder={{ color: 'purple.300' }}
            _focus={{ bg: 'white', borderColor: 'purple.400', boxShadow: '0 0 0 3px rgba(124,58,237,0.1)' }}
          />

          {/* Media previews */}
          {mediaList.length > 0 && (
            <HStack spacing={2} flexWrap="wrap">
              {mediaList.map((m, idx) => (
                <Box key={idx} position="relative" w="80px" h="80px" borderRadius="xl" overflow="hidden"
                  border="2px solid" borderColor="purple.200">
                  {m.media_type === 'image' ? (
                    <img src={m.preview} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  ) : (
                    <video src={m.preview} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  )}
                  <Box position="absolute" top={1} right={1}
                    bg="blackAlpha.700" borderRadius="full" w="18px" h="18px"
                    display="flex" alignItems="center" justifyContent="center"
                    cursor="pointer" onClick={() => removeMedia(idx)}
                    fontSize="10px" color="white">
                    ✕
                  </Box>
                  {m.media_type === 'video' && (
                    <Box position="absolute" bottom={1} left={1}
                      fontSize="10px" bg="blackAlpha.700" borderRadius="md" px={1} color="white">
                      🎬
                    </Box>
                  )}
                </Box>
              ))}
            </HStack>
          )}

          {error && (
            <Text color="red.500" fontSize="12px">{error}</Text>
          )}

          {/* Divider */}
          <Box borderTop="1px solid" borderColor="purple.100" />

          <HStack justify="space-between" align="center">
            <input
              type="file" ref={fileRef}
              style={{ display: 'none' }}
              accept="image/*,video/*" multiple
              onChange={handleFileSelect}
            />
            <HStack spacing={1}>
              <Button size="sm" variant="ghost"
                color="purple.500" fontWeight="600" fontSize="13px"
                borderRadius="xl"
                _hover={{ bg: 'purple.50' }}
                onClick={() => fileRef.current?.click()}>
                📷 படம்
              </Button>
              <Button size="sm" variant="ghost"
                color="purple.500" fontWeight="600" fontSize="13px"
                borderRadius="xl"
                _hover={{ bg: 'purple.50' }}
                onClick={() => fileRef.current?.click()}>
                🎬 வீடியோ
              </Button>
            </HStack>
            <Button size="sm"
              bgGradient="linear(to-r, purple.600, green.500)"
              color="white" borderRadius="xl" fontWeight="700"
              px={5}
              isLoading={posting || uploading}
              loadingText={uploading ? 'Uploading...' : 'Posting...'}
              onClick={handlePost}
              isDisabled={!caption.trim() && mediaList.length === 0}
              _hover={{ opacity: 0.92 }}>
              பகிர் / Share
            </Button>
          </HStack>
        </VStack>
      </HStack>
    </Box>
  );
}

// ─────────────────────────────────────────
// CommentSection — inline comments for a post
// ─────────────────────────────────────────
function CommentSection({ postId, currentUserId, commentCount }) {
  const [open,     setOpen]     = useState(false);
  const [comments, setComments] = useState([]);
  const [loading,  setLoading]  = useState(false);
  const [text,     setText]     = useState('');
  const [sending,  setSending]  = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/api/posts/${postId}/comments`);
      setComments(res.data.comments || []);
    } catch (e) {}
    finally { setLoading(false); }
  };

  const handleOpen = () => {
    setOpen(prev => {
      if (!prev) load();
      return !prev;
    });
  };

  const handleSend = async () => {
    if (!text.trim()) return;
    setSending(true);
    try {
      const res = await api.post(`/api/posts/${postId}/comments`, { content: text.trim() });
      setComments(prev => [...prev, res.data.comment]);
      setText('');
    } catch (e) {}
    finally { setSending(false); }
  };

  const handleDelete = async (commentId) => {
    try {
      await api.delete(`/api/posts/${postId}/comments/${commentId}`);
      setComments(prev => prev.filter(c => c.id !== commentId));
    } catch (e) {}
  };

  return (
    <VStack align="stretch" spacing={0}>
      <Button size="xs" variant="ghost"
        color="gray.400" fontWeight="600"
        _hover={{ color: 'purple.600', bg: 'purple.50' }}
        alignSelf="flex-start"
        onClick={handleOpen}>
        💬 {commentCount > 0 ? `${commentCount} கருத்து` : 'கருத்து சேர்'}
      </Button>

      {open && (
        <VStack align="stretch" spacing={2} mt={2}
          bg="purple.50" borderRadius="xl" p={3}
          border="1px solid" borderColor="purple.100">

          {loading && <Spinner size="sm" color="purple.400" alignSelf="center" />}

          {comments.map(c => (
            <HStack key={c.id} spacing={2} align="flex-start">
              <Avatar size="xs" name={c.user?.name} src={c.user?.profile_photo} />
              <Box flex={1} bg="white" borderRadius="lg" px={3} py={2}
                border="1px solid" borderColor="purple.100">
                <HStack justify="space-between">
                  <Text fontSize="xs" fontWeight="700" color="purple.700">{c.user?.name}</Text>
                  <Text fontSize="9px" color="gray.400">{timeAgo(c.created_at)}</Text>
                </HStack>
                <Text fontSize="xs" color="gray.700" mt={1}>{c.content}</Text>
              </Box>
              {c.user?.id === currentUserId && (
                <Button size="xs" variant="ghost" color="gray.300"
                  _hover={{ color: 'red.400' }} px={1}
                  onClick={() => handleDelete(c.id)}>
                  ✕
                </Button>
              )}
            </HStack>
          ))}

          {/* Add comment input */}
          <HStack spacing={2}>
            <Input
              placeholder="கருத்து எழுதுங்கள்..."
              value={text}
              onChange={e => setText(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') handleSend(); }}
              size="sm"
              bg="white"
              color="purple.900"
              borderColor="purple.200"
              borderRadius="xl"
              fontSize="xs"
              _placeholder={{ color: 'purple.300' }}
              _focus={{ borderColor: 'purple.400', boxShadow: 'none' }}
            />
            <Button size="sm" colorScheme="purple" borderRadius="xl"
              isLoading={sending}
              isDisabled={!text.trim()}
              onClick={handleSend}>
              ↑
            </Button>
          </HStack>
        </VStack>
      )}
    </VStack>
  );
}

// ─────────────────────────────────────────
// PostCard — single post in the feed
// ─────────────────────────────────────────
function PostCard({ post, currentUserId, onDeleted }) {
  const [liked,      setLiked]      = useState(post.did_i_like);
  const [likeCount,  setLikeCount]  = useState(post.like_count);
  const [liking,     setLiking]     = useState(false);
  const [deleting,   setDeleting]   = useState(false);
  const [mediaIndex, setMediaIndex] = useState(0);

  const isOwner = post.user?.id === currentUserId;

  const handleLike = async () => {
    if (liking) return;
    setLiking(true);
    // Optimistic update
    setLiked(prev => !prev);
    setLikeCount(prev => liked ? prev - 1 : prev + 1);
    try {
      const res = await api.post(`/api/posts/${post.id}/like`);
      setLiked(res.data.liked);
      setLikeCount(res.data.like_count);
    } catch (e) {
      // Revert on error
      setLiked(prev => !prev);
      setLikeCount(prev => liked ? prev + 1 : prev - 1);
    } finally { setLiking(false); }
  };

  const handleDelete = async () => {
    if (!window.confirm('இந்த பதிவை நீக்கவா? / Delete this post?')) return;
    setDeleting(true);
    try {
      await api.delete(`/api/posts/${post.id}`);
      onDeleted(post.id);
    } catch (e) {}
    finally { setDeleting(false); }
  };

  const media = post.media || [];

  return (
    <Box
      bg="white"
      border="1.5px solid" borderColor="purple.100"
      borderRadius="2xl" overflow="hidden"
      boxShadow="0 2px 12px rgba(124,58,237,0.07)"
    >

      {/* Post header */}
      <HStack justify="space-between" px={4} pt={4} pb={2}>
        <HStack spacing={3}>
          <Avatar size="sm" name={post.user?.name} src={post.user?.profile_photo}
            border="2px solid" borderColor="purple.300" />
          <Box>
            <Text fontSize="sm" fontWeight="700" color="purple.900">{post.user?.name}</Text>
            <HStack spacing={1}>
              {post.user?.kutham && (
                <Text fontSize="9px" color="purple.500" fontWeight="600">{post.user.kutham}</Text>
              )}
              <Text fontSize="9px" color="gray.400">• {timeAgo(post.created_at)}</Text>
            </HStack>
          </Box>
        </HStack>
        {isOwner && (
          <Button size="xs" variant="ghost" color="gray.300"
            _hover={{ color: 'red.400', bg: 'red.50' }}
            isLoading={deleting}
            onClick={handleDelete}>
            🗑
          </Button>
        )}
      </HStack>

      {/* Caption */}
      {post.caption && (
        <Text fontSize="14px" color="gray.700" px={4} pb={3} lineHeight="1.6">
          {post.caption}
        </Text>
      )}

      {/* Media — white background, no black bars */}
      {media.length > 0 && (
        <Box position="relative" bg="gray.50"
          borderTop="1px solid" borderBottom="1px solid" borderColor="purple.50">
          {media[mediaIndex].media_type === 'image' ? (
            <img
              src={media[mediaIndex].media_url}
              alt=""
              style={{
                width: '100%',
                maxHeight: '420px',
                objectFit: 'contain',
                display: 'block',
                background: '#fafafa',
              }}
            />
          ) : (
            <video
              src={media[mediaIndex].media_url}
              controls
              style={{ width: '100%', maxHeight: '420px', display: 'block' }}
            />
          )}

          {/* Multi-media dots */}
          {media.length > 1 && (
            <HStack position="absolute" bottom={2} left={0} right={0}
              justify="center" spacing={1}>
              {media.map((_, idx) => (
                <Box key={idx}
                  w={idx === mediaIndex ? '16px' : '6px'} h="6px"
                  borderRadius="full"
                  bg={idx === mediaIndex ? 'purple.500' : 'purple.200'}
                  cursor="pointer" transition="all 0.2s"
                  onClick={() => setMediaIndex(idx)} />
              ))}
            </HStack>
          )}

          {/* Prev/Next arrows */}
          {media.length > 1 && mediaIndex > 0 && (
            <Box position="absolute" left={2} top="50%" transform="translateY(-50%)"
              bg="whiteAlpha.800" borderRadius="full" w="28px" h="28px"
              display="flex" alignItems="center" justifyContent="center"
              cursor="pointer" boxShadow="sm"
              onClick={() => setMediaIndex(prev => prev - 1)}
              color="purple.600" fontSize="lg" fontWeight="bold">
              ‹
            </Box>
          )}
          {media.length > 1 && mediaIndex < media.length - 1 && (
            <Box position="absolute" right={2} top="50%" transform="translateY(-50%)"
              bg="whiteAlpha.800" borderRadius="full" w="28px" h="28px"
              display="flex" alignItems="center" justifyContent="center"
              cursor="pointer" boxShadow="sm"
              onClick={() => setMediaIndex(prev => prev + 1)}
              color="purple.600" fontSize="lg" fontWeight="bold">
              ›
            </Box>
          )}
        </Box>
      )}

      {/* Like + Comment actions */}
      <VStack align="stretch" px={4} pt={3} pb={4} spacing={2}>
        <HStack spacing={4}>
          <Button size="sm" variant="ghost"
            color={liked ? 'red.500' : 'gray.400'}
            _hover={{ color: 'red.400', bg: 'red.50' }}
            leftIcon={<Text>{liked ? '❤️' : '🤍'}</Text>}
            onClick={handleLike}
            isLoading={liking}>
            <Text fontSize="sm" color={liked ? 'red.500' : 'gray.500'}>
              {likeCount > 0 ? likeCount : ''}
            </Text>
          </Button>
        </HStack>

        <Divider borderColor="purple.100" />

        <CommentSection
          postId={post.id}
          currentUserId={currentUserId}
          commentCount={post.comment_count}
        />
      </VStack>
    </Box>
  );
}

// ─────────────────────────────────────────
// Main FamilyFeed Component
// ─────────────────────────────────────────
export default function FamilyFeed({ currentUser }) {
  const [posts,    setPosts]    = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [loadMore, setLoadMore] = useState(false);
  const [hasMore,  setHasMore]  = useState(true);
  const [page,     setPage]     = useState(1);
  const [error,    setError]    = useState(null);

  useEffect(() => { loadFeed(1, true); }, [currentUser.id]);

  const loadFeed = async (pageNum = 1, reset = false) => {
    if (pageNum === 1) setLoading(true);
    else setLoadMore(true);
    setError(null);

    try {
      const res = await api.get(`/api/posts/feed?page=${pageNum}&limit=20`);
      const newPosts = res.data.posts || [];

      if (reset) {
        setPosts(newPosts);
      } else {
        setPosts(prev => [...prev, ...newPosts]);
      }

      setHasMore(res.data.has_more);
      setPage(pageNum);
    } catch (e) {
      setError('பதிவுகளை ஏற்றுவதில் பிழை / Error loading feed');
    } finally {
      setLoading(false);
      setLoadMore(false);
    }
  };

  const handlePostCreated = () => {
    // Reload feed from page 1 after new post
    loadFeed(1, true);
  };

  const handlePostDeleted = (postId) => {
    setPosts(prev => prev.filter(p => p.id !== postId));
  };

  return (
    <VStack w="100%" spacing={4} align="stretch">

      {/* Create post box */}
      <CreatePost currentUser={currentUser} onPostCreated={handlePostCreated} />

      {/* Loading state */}
      {loading && (
        <VStack py={10}>
          <Spinner color="purple.300" size="lg" />
          <Text color="whiteAlpha.500" fontSize="sm">பதிவுகள் ஏற்றுகிறோம்...</Text>
        </VStack>
      )}

      {/* Error state */}
      {error && (
        <Text color="red.300" textAlign="center" py={4}>{error}</Text>
      )}

      {/* Empty state */}
      {!loading && !error && posts.length === 0 && (
        <VStack py={10} spacing={3}>
          <Text fontSize="4xl">📭</Text>
          <Text color="whiteAlpha.500" fontSize="sm" textAlign="center">
            இன்னும் பதிவுகள் இல்லை.<br />
            முதல் பதிவை பகிரவும்!
          </Text>
        </VStack>
      )}

      {/* Feed posts */}
      {!loading && posts.map(post => (
        <PostCard
          key={post.id}
          post={post}
          currentUserId={currentUser.id}
          onDeleted={handlePostDeleted}
        />
      ))}

      {/* Load more */}
      {!loading && hasMore && posts.length > 0 && (
        <Button
          variant="ghost" color="whiteAlpha.500"
          _hover={{ color: 'white' }}
          isLoading={loadMore}
          onClick={() => loadFeed(page + 1, false)}>
          மேலும் பதிவுகள் / Load more
        </Button>
      )}

    </VStack>
  );
}
